import { create } from 'zustand';

/**
 * The warm-up steps, in the order they run, weighted by roughly how long each
 * one takes. The weights only decide how the bar is divided up — nothing waits
 * on them — so an approximation that keeps it moving evenly is enough.
 */
export const LOAD_STEPS = {
  assets: { weight: 4, label: 'Loading assets' },
  samples: { weight: 1, label: 'Loading samples' },
  scene: { weight: 1, label: 'Building scene' },
  shaders: { weight: 4, label: 'Compiling shaders' },
} as const;

export type LoadStep = keyof typeof LOAD_STEPS;

const STEP_ORDER = Object.keys(LOAD_STEPS) as LoadStep[];
const TOTAL_WEIGHT = STEP_ORDER.reduce((sum, step) => sum + LOAD_STEPS[step].weight, 0);

type Steps = Record<LoadStep, number>;

interface LoadStore {
  /** How far each step has got, 0 to 1. */
  steps: Steps;
  /** The whole warm-up as one number, for the bar. */
  progress: number;
  /** What the loader is waiting on right now. */
  label: string;
  /** Every file the scene needs is in the browser cache and can be parsed. */
  assetsReady: boolean;

  setStep: (step: LoadStep, ratio: number) => void;
  assetsLoaded: () => void;
}

function summarise(steps: Steps): Pick<LoadStore, 'progress' | 'label'> {
  const weighted = STEP_ORDER.reduce((sum, step) => sum + LOAD_STEPS[step].weight * steps[step], 0);
  // The last step is the one being reported once everything is done, so the
  // label doesn't blank out on the final frame before the loader leaves.
  const current = STEP_ORDER.find((step) => steps[step] < 1) ?? STEP_ORDER[STEP_ORDER.length - 1];
  return { progress: weighted / TOTAL_WEIGHT, label: LOAD_STEPS[current].label };
}

const EMPTY = Object.fromEntries(STEP_ORDER.map((step) => [step, 0])) as Steps;

/**
 * How far the warm-up has got. Written from outside React — by the preloader
 * and by the scene's own warm-up pass — and read only by the loader.
 */
export const useLoadStore = create<LoadStore>((set, get) => ({
  steps: EMPTY,
  ...summarise(EMPTY),
  assetsReady: false,

  setStep: (step, ratio) => {
    // Progress that only ever climbs: several files report into one step at
    // their own pace, and a bar that slips backwards reads as a stall.
    const next = Math.min(1, Math.max(get().steps[step], ratio));
    if (next === get().steps[step]) return;
    const steps = { ...get().steps, [step]: next };
    set({ steps, ...summarise(steps) });
  },

  assetsLoaded: () => set({ assetsReady: true }),
}));
